export const FEATURE_KEYS = ['dashboard', 'projects', 'estimates', 'design', 'labor', 'admin'];

export const DEFAULT_FEATURE_FLAGS = {
  dashboard: true,
  projects: true,
  estimates: true,
  design: true,
  labor: true,
  admin: true,
};

const PLAN_FEATURE_MATRIX = {
  trial: { ...DEFAULT_FEATURE_FLAGS },
  starter: { ...DEFAULT_FEATURE_FLAGS },
  pro: { ...DEFAULT_FEATURE_FLAGS },
  growth: { ...DEFAULT_FEATURE_FLAGS }, // legacy alias to be removed after migration
  enterprise: { ...DEFAULT_FEATURE_FLAGS },
};

const ROLE_RULES = {
  admin: { canInvite: true, canManageOrg: true, canDeleteProject: true },
  estimator: { canInvite: false, canManageOrg: false, canDeleteProject: false },
  designer: { canInvite: false, canManageOrg: false, canDeleteProject: false },
  viewer: { canInvite: false, canManageOrg: false, canDeleteProject: false },
};

export const PLAN_LABELS = {
  trial: 'Trial',
  starter: 'Starter',
  pro: 'Pro',
  growth: 'Pro',
  enterprise: 'Enterprise',
};

export function ensurePlan(value) {
  const normalized = (value || '').toLowerCase();
  if (normalized === 'growth') return 'pro';
  if (normalized === 'diy') return 'starter';
  return ['trial', 'starter', 'pro', 'enterprise'].includes(normalized) ? normalized : 'trial';
}

export function ensureRole(value) {
  const normalized = (value || '').toLowerCase();
  return ['admin', 'estimator', 'designer', 'viewer'].includes(normalized) ? normalized : 'viewer';
}

const FEATURE_KEY_ALIASES = {
  uploads: 'estimates',
  xr: 'design',
};

function normalizeFlags(flags) {
  if (!flags || typeof flags !== 'object') return [];

  return Object.entries(flags)
    .map(([rawKey, value]) => {
      const key = FEATURE_KEYS.includes(rawKey) ? rawKey : FEATURE_KEY_ALIASES[rawKey] || null;
      if (!key || !FEATURE_KEYS.includes(key)) return null;
      return [key, Boolean(value)];
    })
    .filter(Boolean);
}

export function buildPermissions({ role = 'viewer', plan = 'trial', orgFlags, memberOverrides } = {}) {
  const safeRole = ensureRole(role);
  const safePlan = ensurePlan(plan);
  const merged = { ...PLAN_FEATURE_MATRIX[safePlan] };

  normalizeFlags(orgFlags).forEach(([key, value]) => {
    if (value === true) {
      merged[key] = true;
    }
  });
  normalizeFlags(memberOverrides).forEach(([key, value]) => {
    merged[key] = value;
  });

  const rules = ROLE_RULES[safeRole] || ROLE_RULES.viewer;
  const canAccessDashboard = Boolean(merged.dashboard);
  const canAccessProjects = Boolean(merged.projects);
  const canAccessEstimates = Boolean(merged.estimates);
  const canAccessDesign = Boolean(merged.design);
  const canAccessLabor = Boolean(merged.labor);
  const canAccessAdmin = (Boolean(merged.admin) && rules.canManageOrg) || safeRole === 'admin';

  return {
    role: safeRole,
    plan: safePlan,
    featureFlags: merged,
    canInvite: rules.canInvite,
    canManageOrg: rules.canManageOrg,
    canDeleteProject: rules.canDeleteProject && merged.projects,
    canUpload: merged.estimates && safeRole !== 'viewer',
    canAccessDashboard,
    canAccessProjects,
    canAccessEstimates,
    canAccessDesign,
    canAccessLabor,
    canAccessAdmin,
  };
}

export function hasFeature(permissions, key) {
  return Boolean(permissions?.featureFlags?.[key]);
}
